import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { AdminScopeSnapshot, AdminScopeType } from '../models/AdminScope';
import { OperationGroup } from '../models/OperationGroup';
import { Store } from '../models/Store';
import { adminScopeService, ADMIN_SCOPE_CHANGE_EVENT } from '../services/adminScopeService';
import { operationGroupService } from '../services/operationGroupService';
import { storeService } from '../services/storeService';

interface AdminScopeContextValue {
  loading: boolean;
  stores: Store[];
  operationGroups: OperationGroup[];
  activeScopeType: AdminScopeType;
  selectedGroupId: string | null;
  selectedStoreId: string | null;
  selectedGroup: OperationGroup | null;
  selectedStore: Store | null;
  availableStoresForSelectedGroup: Store[];
  setActiveScopeType: (scopeType: AdminScopeType) => void;
  setSelectedGroupId: (groupId: string | null) => void;
  setSelectedStoreId: (storeId: string | null) => void;
  refreshScopeData: () => Promise<void>;
}

const AdminScopeContext = createContext<AdminScopeContextValue | undefined>(undefined);

const resolveInitialState = (
  stores: Store[],
  operationGroups: OperationGroup[]
): AdminScopeSnapshot => {
  const stored = adminScopeService.getSnapshot();
  const storedStore: Store | null =
    stores.find((store) => store.id === stored.selectedStoreId) || null;
  const storedGroup: OperationGroup | null =
    operationGroups.find((group) => group.id === stored.selectedGroupId) || null;
  const groupFromStoredStore: OperationGroup | null =
    operationGroups.find((group) => group.id === storedStore?.operationGroupId) || null;

  if (stored.activeScopeType === 'store') {
    const fallbackStore =
      storedStore ||
      stores.find((store) => store.isActive) ||
      stores[0] ||
      null;
    const fallbackGroup =
      operationGroups.find((group) => group.id === fallbackStore?.operationGroupId) ||
      storedGroup ||
      operationGroups.find((group) => group.isActive) ||
      operationGroups[0] ||
      null;

    if (!fallbackStore) {
      return {
        activeScopeType: fallbackGroup ? 'group' : 'global',
        selectedGroupId: fallbackGroup?.id || null,
        selectedStoreId: null,
      };
    }

    return {
      activeScopeType: 'store',
      selectedGroupId: fallbackGroup?.id || null,
      selectedStoreId: fallbackStore.id,
    };
  }

  if (stored.activeScopeType === 'group') {
    const fallbackGroup =
      storedGroup ||
      groupFromStoredStore ||
      operationGroups.find((group) => group.isActive) ||
      operationGroups[0] ||
      null;

    if (!fallbackGroup) {
      return {
        activeScopeType: 'global',
        selectedGroupId: null,
        selectedStoreId: storedStore?.id || null,
      };
    }

    const scopedStore =
      storedStore && storedStore.operationGroupId === fallbackGroup.id ? storedStore : null;

    return {
      activeScopeType: 'group',
      selectedGroupId: fallbackGroup.id,
      selectedStoreId: scopedStore?.id || null,
    };
  }

  return {
    activeScopeType: 'global',
    selectedGroupId: storedGroup?.id || groupFromStoredStore?.id || null,
    selectedStoreId: storedStore?.id || null,
  };
};

export const AdminScopeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [stores, setStores] = useState<Store[]>([]);
  const [operationGroups, setOperationGroups] = useState<OperationGroup[]>([]);
  const [activeScopeType, setActiveScopeTypeState] = useState<AdminScopeType>('store');
  const [selectedGroupId, setSelectedGroupIdState] = useState<string | null>(null);
  const [selectedStoreId, setSelectedStoreIdState] = useState<string | null>(null);

  const refreshScopeData = async () => {
    setLoading(true);
    try {
      const [storesData, groupsData] = await Promise.all([
        storeService.getAllStores(),
        operationGroupService.getAllGroups(),
      ]);

      setStores(storesData);
      setOperationGroups(groupsData);

      const resolved = resolveInitialState(storesData, groupsData);
      setActiveScopeTypeState(resolved.activeScopeType);
      setSelectedGroupIdState(resolved.selectedGroupId);
      setSelectedStoreIdState(resolved.selectedStoreId);
      adminScopeService.setSnapshot(resolved);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refreshScopeData();
  }, []);

  useEffect(() => {
    const syncFromStorage = (event: Event) => {
      const detail = (event as CustomEvent).detail;
      if (!detail) return;
      setActiveScopeTypeState(detail.activeScopeType);
      setSelectedGroupIdState(detail.selectedGroupId);
      setSelectedStoreIdState(detail.selectedStoreId);
    };

    if (typeof window !== 'undefined') {
      window.addEventListener(ADMIN_SCOPE_CHANGE_EVENT, syncFromStorage as EventListener);
      return () => {
        window.removeEventListener(ADMIN_SCOPE_CHANGE_EVENT, syncFromStorage as EventListener);
      };
    }

    return undefined;
  }, []);

  const selectedStore = useMemo(
    () => stores.find((store) => store.id === selectedStoreId) || null,
    [stores, selectedStoreId]
  );

  const selectedGroup = useMemo(
    () => operationGroups.find((group) => group.id === selectedGroupId) || null,
    [operationGroups, selectedGroupId]
  );

  const availableStoresForSelectedGroup = useMemo(() => {
    if (!selectedGroupId) {
      return stores;
    }
    return stores.filter((store) => store.operationGroupId === selectedGroupId);
  }, [stores, selectedGroupId]);

  const setActiveScopeType = (scopeType: AdminScopeType) => {
    setActiveScopeTypeState(scopeType);
    adminScopeService.setActiveScopeType(scopeType);
  };

  const setSelectedGroupId = (groupId: string | null) => {
    setSelectedGroupIdState(groupId);
    let nextStoreId: string | null = selectedStoreId;

    if (!groupId) {
      nextStoreId = null;
      setSelectedStoreIdState(null);
    } else {
      const storesForGroup = stores.filter((store) => store.operationGroupId === groupId);
      if (nextStoreId && !storesForGroup.some((store) => store.id === nextStoreId)) {
        nextStoreId = null;
        setSelectedStoreIdState(null);
      }
    }

    adminScopeService.setSnapshot({
      selectedGroupId: groupId,
      selectedStoreId: nextStoreId,
    });
  };

  const setSelectedStoreId = (storeId: string | null) => {
    const nextStore = stores.find((store) => store.id === storeId) || null;
    const nextGroupId = nextStore?.operationGroupId || selectedGroupId || null;

    setSelectedStoreIdState(storeId);
    setSelectedGroupIdState(nextGroupId);

    adminScopeService.setSnapshot({
      selectedStoreId: storeId,
      selectedGroupId: nextGroupId,
    });
  };

  const value = useMemo<AdminScopeContextValue>(
    () => ({
      loading,
      stores,
      operationGroups,
      activeScopeType,
      selectedGroupId,
      selectedStoreId,
      selectedGroup,
      selectedStore,
      availableStoresForSelectedGroup,
      setActiveScopeType,
      setSelectedGroupId,
      setSelectedStoreId,
      refreshScopeData,
    }),
    [
      loading,
      stores,
      operationGroups,
      activeScopeType,
      selectedGroupId,
      selectedStoreId,
      selectedGroup,
      selectedStore,
      availableStoresForSelectedGroup,
      setSelectedGroupId,
      setSelectedStoreId,
      refreshScopeData,
    ]
  );

  return <AdminScopeContext.Provider value={value}>{children}</AdminScopeContext.Provider>;
};

export const useAdminScope = (): AdminScopeContextValue => {
  const context = useContext(AdminScopeContext);
  if (!context) {
    throw new Error('useAdminScope must be used within an AdminScopeProvider');
  }
  return context;
};
